import MobilePageContent from 'app/pages/mobile/mobile-layout/MobilePageContent';
import { Box, IconButton, Tab } from '@mui/material';
import SearchIcon from '@mui/icons-material/Search';
import { TabContext, TabList, TabPanel } from '@mui/lab';
import MobileSearchBar from 'app/pages/mobile/components/MobileSearchBar';
import { BACKGROUND_SIZES_v2, MOBILE_NAV_HEIGHT_NUMBER } from 'app/pages/mobile/utils/constants';
import { useState, useEffect, useCallback, useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { ReimbursementTransactionStatus, ReimbursementTransaction } from 'API';
import {
  REIMBURSEMENT_TABS,
  ReimbursementTabType,
  formatTransactionType,
} from 'app/pages/overview/tabs/reimbursements/utils';
import { useAppSelector } from 'app/store';
import { selectUserCompanies } from 'app/store/userCompaniesSlice';
import { getReimbursementTransactionsByPaidolId } from 'app/services/reimbursementService';
import { useSnackbar } from 'notistack';
import { captureException } from '@sentry/react';
import { isNotNullOrUndefined } from 'util/typeGuards';
import MobileReimbursementItem from './MobileReimbursementItem';
import MobileLoadingOverlay from 'app/pages/mobile/components/MobileLoadingOverlay';
import { useUserRole } from 'app/hooks/useUserRole';
import { ReactComponent as PlusItem } from 'assets/PlusItem.svg';
import MobileAddReimbursement from './MobileAddReimbursement';
import MobileReimbursementDetails from './MobileReimbursementDetails';

const MobileReimbursements = () => {
  const { t } = useTranslation();
  const { enqueueSnackbar } = useSnackbar();
  const [tab, setTab] = useState<ReimbursementTransactionStatus>(ReimbursementTransactionStatus.REQUESTED);
  const [loading, setLoading] = useState(false);
  const [reimbursements, setReimbursements] = useState<ReimbursementTransaction[]>([]);
  const [isAddReimbursementOpen, setIsAddReimbursementOpen] = useState(false);
  const [isReimbursementDetailsOpen, setIsReimbursementDetailsOpen] = useState(false);
  const [selectedReimbursementId, setSelectedReimbursementId] = useState<string | undefined>(undefined);
  const [searchOpen, setSearchOpen] = useState(false);
  const [searchValue, setSearchValue] = useState('');

  const { selectedPaidolId, paidolUsers } = useAppSelector(selectUserCompanies);
  // SEE-1223: Approval admins share the admin reimbursements queue with
  // Primary (matrix: final approver), matching the desktop Overview gate.
  // SEE-1217: canSubmitReimbursement excludes Secondary admins, who have no
  // submit surface on web.
  // SEE-943: canAccessAdminReimbursements now also covers Group Admins (web
  // review queue). Mobile keeps them on the personal view, so the mobile
  // queue gate is the paidol-wide set (canExportReimbursements).
  const { canExportReimbursements, canSubmitReimbursement } = useUserRole();

  const toggleSearch = () =>
    setSearchOpen((open) => {
      if (open) setSearchValue('');
      return !open;
    });
  const currentPaidolUserId = useMemo(() => {
    if (!selectedPaidolId || !paidolUsers || paidolUsers.length === 0) {
      return undefined;
    }
    const matchingPaidolUser = paidolUsers.find((pu) => pu.paidol_id === selectedPaidolId);
    return matchingPaidolUser?.id;
  }, [selectedPaidolId, paidolUsers]);

  // The Exported tab belongs to the admin reimbursements queue (Primary +
  // Secondary + Approval admins); everyone else sees only their personal tabs.
  const reimbursementTabs = REIMBURSEMENT_TABS.filter(
    (tab) => canExportReimbursements || tab.status !== ReimbursementTransactionStatus.EXPORTED
  ).map((tab) => ({
    ...tab,
    label: t(tab.label),
  })) as ReimbursementTabType[];

  const displayedReimbursements = useMemo(() => {
    const search = searchValue.trim().toLowerCase();
    if (!search) return reimbursements;
    return reimbursements.filter((r) => {
      const merchant = r.merchantName?.toLowerCase() || '';
      const amount = r.amount?.value?.toString() || '';
      const type = t(formatTransactionType(r.transactionType)).toLowerCase();
      const created = r.createdAt ? new Date(r.createdAt).toLocaleDateString().toLowerCase() : '';
      const decided = r.decisionDate ? new Date(r.decisionDate).toLocaleDateString().toLowerCase() : '';
      return (
        merchant.includes(search) ||
        amount.includes(search) ||
        type.includes(search) ||
        created.includes(search) ||
        decided.includes(search)
      );
    });
  }, [reimbursements, searchValue, t]);

  const getReimbursements = useCallback(
    async (paidolId: string, status: ReimbursementTransactionStatus, paidolUserId?: string) => {
      setLoading(true);

      try {
        // Match web app behavior:
        // - Cardholders (user view): pass paidolUserId to see only their reimbursements
        // - Admins (admin view): don't pass paidolUserId to see all reimbursements
        const response = await getReimbursementTransactionsByPaidolId(paidolId, status, paidolUserId);

        if (response?.items) {
          const filteredItems = response.items.filter(isNotNullOrUndefined);
          setReimbursements(filteredItems);
        } else {
          setReimbursements([]);
        }
      } catch (error) {
        captureException(error);
        enqueueSnackbar(t('reimbursementPage.messages.loadingError'), {
          variant: 'error',
        });
        setReimbursements([]);
      } finally {
        setLoading(false);
      }
    },
    [enqueueSnackbar, t]
  );

  // Load reimbursements when tab changes or when paidolId/paidolUserId changes
  // Match web app behavior:
  // - Administrators: don't pass paidolUserId (admin view - see all reimbursements)
  // - Group Admins and Cardholders: pass paidolUserId (user view - see only their reimbursements)
  useEffect(() => {
    if (selectedPaidolId) {
      if (
        tab === ReimbursementTransactionStatus.REQUESTED ||
        tab === ReimbursementTransactionStatus.REVIEWED ||
        tab === ReimbursementTransactionStatus.APPROVED ||
        tab === ReimbursementTransactionStatus.EXPORTED ||
        tab === ReimbursementTransactionStatus.DENIED
      ) {
        // Admin-queue roles (Primary + Approval + Secondary) don't pass
        // paidolUserId (see all); everyone else (cardholder, group admin) gets
        // the personal view.
        if (canExportReimbursements) {
          getReimbursements(selectedPaidolId, tab);
        } else if (currentPaidolUserId) {
          getReimbursements(selectedPaidolId, tab, currentPaidolUserId);
        }
      } else {
        // Clear reimbursements for tabs that aren't implemented yet
        setReimbursements([]);
      }
    }
  }, [selectedPaidolId, tab, getReimbursements, canExportReimbursements, currentPaidolUserId]);

  const handleReimbursementClick = (reimbursementId: string) => {
    setSelectedReimbursementId(reimbursementId);
    setIsReimbursementDetailsOpen(true);
  };

  const handleCloseReimbursementDetails = () => {
    setIsReimbursementDetailsOpen(false);
    setSelectedReimbursementId(undefined);
  };

  const handleAddReimbursementClick = () => {
    setIsAddReimbursementOpen(true);
  };

  const handleCloseAddReimbursement = () => {
    setIsAddReimbursementOpen(false);
  };

  const handleNextStep = () => {
    // Refresh the reimbursements list after successful submission
    if (selectedPaidolId) {
      if (canExportReimbursements) {
        getReimbursements(selectedPaidolId, tab);
      } else if (currentPaidolUserId) {
        getReimbursements(selectedPaidolId, tab, currentPaidolUserId);
      }
    }
  };

  return (
    <>
      {/* Main reimbursements list */}
      <MobileLoadingOverlay isLoading={loading} />
      <MobilePageContent
        transparentHeight={BACKGROUND_SIZES_v2.SHOW_HEADER}
        title="Reimbursements"
        showCompanySwitcher
        sx={{ pt: 1 }}
        headerRight={
          <IconButton
            onClick={toggleSearch}
            aria-label={t('mobileSearch')}
            sx={{ color: 'common.white', p: 0 }}
          >
            <SearchIcon />
          </IconButton>
        }
      >
        <TabContext value={tab}>
          <Box sx={{ bgcolor: 'neutral95.main', borderRadius: 2, px: 1.5, pt: 0.5, mb: 2 }}>
            <TabList
              onChange={(event, value) => setTab(value)}
              TabIndicatorProps={{ children: <span className="MuiTabs-indicatorSpan" /> }}
              sx={{
                minHeight: 0,
                // Size tabs to their label and spread evenly so inter-label gaps match.
                '& .MuiTabs-flexContainer': { justifyContent: 'space-between' },
                // Shorter active-tab underline (a bit narrower than the label), centered.
                '& .MuiTabs-indicator': {
                  display: 'flex',
                  justifyContent: 'center',
                  backgroundColor: 'transparent',
                },
                '& .MuiTabs-indicatorSpan': {
                  width: 'calc(100% - 16px)',
                  height: 3,
                  borderRadius: 1.5,
                  backgroundColor: 'primary.dark',
                },
                '& .MuiTab-root': {
                  minHeight: 0,
                  minWidth: 0,
                  px: 0.5,
                  py: 1.75,
                  textTransform: 'none',
                  fontWeight: 500,
                  fontSize: '13px',
                  color: 'neutralVariant25.main',
                },
                '& .Mui-selected': { color: 'primary.dark' },
              }}
            >
              {reimbursementTabs.map((tabItem) => (
                <Tab key={tabItem.status} label={tabItem.label} value={tabItem.status} />
              ))}
            </TabList>
          </Box>

          {searchOpen && (
            <Box sx={{ mb: 2 }}>
              <MobileSearchBar value={searchValue} onChange={setSearchValue} />
            </Box>
          )}

          {reimbursementTabs.map((tabItem) => (
            <TabPanel key={tabItem.status} value={tabItem.status} sx={{ p: 0, position: 'relative' }}>
              {(tabItem.status === ReimbursementTransactionStatus.REQUESTED ||
                tabItem.status === ReimbursementTransactionStatus.REVIEWED ||
                tabItem.status === ReimbursementTransactionStatus.APPROVED ||
                tabItem.status === ReimbursementTransactionStatus.EXPORTED ||
                tabItem.status === ReimbursementTransactionStatus.DENIED) &&
                !loading && (
                  <>
                    {displayedReimbursements.length > 0 && (
                      <Box sx={{ bgcolor: 'neutral95.main', borderRadius: 2, overflow: 'hidden' }}>
                        {displayedReimbursements.map((reimbursement) => (
                          <MobileReimbursementItem
                            key={reimbursement.id}
                            reimbursement={reimbursement}
                            onReimbursementClick={handleReimbursementClick}
                            status={tabItem.status}
                          />
                        ))}
                      </Box>
                    )}
                    {displayedReimbursements.length === 0 && (
                      <Box sx={{ textAlign: 'center', py: 4, color: 'text.secondary' }}>
                        {t('reimbursementsEmptyState.header')}
                      </Box>
                    )}
                  </>
                )}

              {/* Submit FAB — hidden for Secondary admins, who have no submit
                  surface on web. */}
              {tabItem.status === ReimbursementTransactionStatus.REQUESTED && canSubmitReimbursement && (
                <Box
                  onClick={handleAddReimbursementClick}
                  sx={{
                    position: 'fixed',
                    bottom: MOBILE_NAV_HEIGHT_NUMBER + 16, // Above the nav bar with padding
                    right: 16,
                    zIndex: 1000,
                    cursor: 'pointer',
                    '&:hover': {
                      opacity: 0.8,
                    },
                  }}
                  aria-label="Add reimbursement"
                >
                  <PlusItem style={{ height: '75px', width: '75px', display: 'block' }} />
                </Box>
              )}
            </TabPanel>
          ))}
        </TabContext>
      </MobilePageContent>

      {/* Add Reimbursement Modal */}
      <MobileAddReimbursement
        open={isAddReimbursementOpen}
        onClose={handleCloseAddReimbursement}
        onNext={handleNextStep}
      />

      {/* Reimbursement Details */}
      <MobileReimbursementDetails
        open={isReimbursementDetailsOpen}
        onClose={handleCloseReimbursementDetails}
        reimbursementId={selectedReimbursementId}
      />
    </>
  );
};

export default MobileReimbursements;
